/*
 * ALLOCATIONS-VIEW.JSX - All Users Timesheet Allocations Container
 *
 * SUMMARY:
 * - Renders allocations table with approve/reject/edit/delete actions and bulk operations.
 * - Reuses ViewTimeSheet table structure for a multi-user, filter-driven view.
 */

import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getResourceAllocations,
    approveTimesheet,
    deleteResource,
    bulkUpdateTimesheet,
    deleteResources,
    recalculateTimesheets,
} from "../../actions/timesheet.action";
import { getUserNames, getUser } from "../../actions/user-management.action";
import { getProjectList } from "../../actions/project-management.action";
import EditTimesheetDialog from "./components/edit-timesheet-dialog";
import DeleteDialog from "./../../components/delete-dialog";
import TimesheetActionConfirmation from "./../../components/timesheet-action-confirmation-dialog";
import ReviewCommentModal from "./../../components/review-comment-modal";
import Modal from "./../../components/modal/modal";
import { Grid, Menu, MenuItem, Tabs, Tab } from "@mui/material";
import CustomTable from "../../components/table/table";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Form from "react-bootstrap/Form";
import BulkAllocationModal from "../../components/bulk-allocation-dialog";
import Filter from "./filter";
import idx from "idx";
import moment from "moment";
import { errorSnackBar, successSnackBar, warningSnackBar } from "../../actions/common.action";
import BulkApproveDialog from "./bulk-approve-dialog";
import { Button } from "@material-ui/core";
import CheckIcon from "@mui/icons-material/Check";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ManualReviewHoursModal from "./components/ManualReviewHoursModal";
import TimesheetReportTable from "./components/timesheet-report-table";
import { renderBreakdownCell, aggregateTimesheetReport } from "./util/timesheet-breakdown";
import { numberFormat } from "../../util/currency-formatter";
import "./scss/timesheet.scss";

const STATUS_OPTIONS = [
    { key: "All", value: "all" },
    { key: "Pending", value: "PENDING" },
    { key: "Approved", value: "APPROVED" },
    { key: "Rejected", value: "REJECTED" },
];

/**
 * All Users Timesheet Allocations View. Renders allocations table with approve,
 * reject, edit, delete actions and bulk operations across users and projects.
 *
 * @returns {React.ReactElement} Rendered allocations view
 */
const AllocationsView = () => {
    const dispatch = useDispatch();
    const { permissions, id: managerId } = useSelector((state) => state.user.userData);
    const projectCategories = useSelector((state) => state.project.projectCategories) || [];

    const [activeTab, setActiveTab] = useState("allocations");
    const [allocations, setAllocations] = useState([]);
    const [columns, setColumns] = useState([]);
    const [showAllocateResource, setShowAllocateResource] = useState(false);
    const [timesheetStatus, setTimesheetStatus] = useState("");
    const [deleteDialogState, setDeleteDialogState] = useState(false);
    const [actionConfirmationState, setActionConfirmationState] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [allocationId, setAllocationId] = useState(null);
    const [selectedCount, setSelectedCount] = useState(0);
    const [bulkAllocationModal, setBulkAllocationModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [filterParams, setFilterParams] = useState(null);
    const [projectData, setProjectData] = useState(null);
    const [resourceOptions, setResourceOptions] = useState([{ key: "All", value: "all" }]);
    const [reviewCommentModalOpen, setReviewCommentModalOpen] = useState(false);
    const [pendingCommentAction, setPendingCommentAction] = useState(null);
    const [reviewComment, setReviewComment] = useState("");
    const [isBulkContext, setIsBulkContext] = useState(false);
    const [showBulkApproveDialog, setShowBulkApproveDialog] = useState(false);
    const [bulkApproveTimesheets, setBulkApproveTimesheets] = useState([]);
    const [bulkApproveTitle, setBulkApproveTitle] = useState("");
    const [viewCommentModalOpen, setViewCommentModalOpen] = useState(false);
    const [selectedComment, setSelectedComment] = useState("");
    const [isViewMode, setIsViewMode] = useState(false);
    const [manualReviewModalOpen, setManualReviewModalOpen] = useState(false);
    const [manualReviewAllocation, setManualReviewAllocation] = useState(null);
    const [recalculateConfirmationOpen, setRecalculateConfirmationOpen] = useState(false);
    const [recalculateIds, setRecalculateIds] = useState([]);
    const [isRecalculating, setIsRecalculating] = useState(false);

    const reportData = useMemo(
        () => aggregateTimesheetReport(allocations),
        [allocations]
    );

    useEffect(() => {
        getColumns();
    }, []);

    useEffect(() => {
        if (filterParams) {
            getAllocations();
        }
    }, [filterParams]);

    useEffect(() => {
        if (!allocations.length) return;
        setSelectedCount(allocations.filter((row) => row.status === "PENDING" && row.isSelected).length);
    }, [allocations]);

    /**
     * Synchronizes bulk selection state across pending allocations in a single state update.
     */
    useEffect(() => {
        if (!allocations.length) return;
        setAllocations((prevAllocations) =>
            prevAllocations.map((allocation) =>
                allocation.status === "PENDING" ? { ...allocation, isSelected: selectAll } : allocation
            )
        );
    }, [selectAll]);

    const getColumns = () => {
        const cols = [
            {
                title: "",
                field: "selectall",
                render: (rowData) => {
                    if (rowData.isHeader) {
                        const pendingRows = rowData.rows.filter((_) => _.status === "PENDING");
                        const isAllSelected = pendingRows.length > 0 && pendingRows.every((_) => _.isSelected);
                        return (
                            <div className="table-checkbox table-checkbox-ch text-left">
                                <Form.Check
                                    type={"checkbox"}
                                    checked={isAllSelected}
                                    value={isAllSelected}
                                    onChange={() => handleSelectAll()}
                                    disabled={pendingRows.length === 0}
                                />
                            </div>
                        );
                    }
                    if (rowData.status !== "PENDING" || !rowData.id) return;
                    return (
                        <div className="table-checkbox table-checkbox-ch text-left">
                            <Form.Check
                                id={rowData.id}
                                type={"checkbox"}
                                label={rowData.description}
                                value={rowData.isSelected}
                                checked={Boolean(rowData.isSelected)}
                                onChange={(e) => handleBulkUpdate(rowData.id, e.target.checked)}
                            />
                        </div>
                    );
                },
            },
            {
                title: "Emp Name",
                field: "empName",
                render: (rowData) => {
                    const isMobile = rowData.createdFrom === "mobile";
                    return (
                        <div className="timesheet-emp-cell">
                            <span>{rowData.empName}</span>
                            {rowData.isGlobal && (
                                <span className="timesheet-global-user-tag"> (Global User)</span>
                            )}
                            {isMobile && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Popover id={`popover-mobile-${rowData.id}`}>
                                            <Popover.Body style={{ fontSize: "11px", padding: "4px 8px" }}>
                                                Submitted from mobile app
                                            </Popover.Body>
                                        </Popover>
                                    }
                                >
                                    <span className="timesheet-mobile-pill">
                                        <PhoneIphoneIcon className="mobile-icon" />
                                    </span>
                                </OverlayTrigger>
                            )}
                        </div>
                    );
                },
            },
            { title: "Project Name", field: "projectName" },
            {
                title: "Section\\Task",
                field: "f_taskName",
                class: "text-uppercase",
            },
            {
                title: "Status",
                field: "status",
                render: (rowData) => {
                    const getStatusBadgeClass = (status) => {
                        switch (status?.toUpperCase()) {
                            case "PENDING":
                                return "status-pending";
                            case "APPROVED":
                                return "status-approved";
                            case "REJECTED":
                                return "status-rejected";
                            default:
                                return "";
                        }
                    };

                    const isConfirmed = Boolean(rowData.isConfirmedByStaff);
                    const isManualReview = rowData.status === "PENDING" && Boolean(rowData.requiresManualReview);

                    return (
                        <div className="timesheet-status-col">
                            {/* Original Primary Status Badge */}
                            {isManualReview ? (
                                <span
                                    className="timesheet-manual-review-pill"
                                    title="This record requires manual intervention"
                                >
                                    MANUAL REVIEW
                                </span>
                            ) : (
                                <span className={`timesheet-status-badge ${getStatusBadgeClass(rowData.status)}`}>
                                    {rowData.status}
                                </span>
                            )}

                            {/* Clean Confirmation Sub-text (Confirmed / Unconfirmed) */}
                            {isConfirmed ? (
                                <span className="timesheet-confirmed-badge">
                                    <CheckIcon className="badge-icon" /> Confirmed
                                </span>
                            ) : (
                                <span className="timesheet-unconfirmed-badge">
                                    <HourglassEmptyIcon className="badge-icon" /> Unconfirmed
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                title: "Start & End time",
                field: "allocationTime",
                defaultSort: "desc",
            },
            {
                title: "Breakdown",
                field: "work_hour_breakdown",
                render: renderBreakdownCell,
            },
            {
                title: "",
                field: "createdAt",
                dataCy: "allocation-info-col",
                render: (rowData) => {
                    const formatAuditTime = (value) =>
                        value ? moment.utc(value).local().format("MMM D, YYYY HH:mm") : "—";
                    const auditRows = [
                        { label: "Created At", value: formatAuditTime(rowData.createdAt) },
                        { label: "Created By", value: rowData.createdByName || "—" },
                        { label: "Updated At", value: formatAuditTime(rowData.updatedAt) },
                        { label: "Updated By", value: rowData.updatedByName || "—" },
                    ];
                    return (
                        <OverlayTrigger
                            trigger={["hover", "focus"]}
                            placement="left"
                            overlay={
                                <Popover id={`popover-audit-${rowData.id}`} className="allocation-audit-popover">
                                    <Popover.Body>
                                        {auditRows.map(({ label, value }) => (
                                            <div className="audit-info-row" key={label}>
                                                <span className="audit-info-label">{label}</span>
                                                <span className="audit-info-value">{value}</span>
                                            </div>
                                        ))}
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            <span className="allocation-info-icon" tabIndex={0}>
                                <InfoOutlinedIcon className='w-15 h-auto'/>
                            </span>
                        </OverlayTrigger>
                    );
                },
            },
        ];
        setColumns(cols);
    };

    const handleSelectAll = () => {
        setSelectAll((prev) => !prev);
    };

    const applyFilter = (fields) => {
        const projectType = fields.projectType.value === "all" ? null : fields.projectType.value;
        const projectId = fields.project.value === "all" ? null : fields.project.value;
        const resourceId = fields.resource?.value || "all";
        const status = fields.status?.value || "all";
        const confirmationStatus = fields.confirmationStatus?.value || "all";
        const createdFrom = fields.createdFrom?.value || "all";
        const startTime = fields.startDate?.value || null;
        const endTime = fields.endDate?.value || null;

        setFilterParams({
            projectType,
            projectId,
            resourceId,
            status,
            confirmationStatus,
            createdFrom,
            startTime,
            endTime,
        });
    };

    const getAllocations = async () => {
        const writePermission = permissions.timesheetApprover === 2;

        if (!permissions.timesheetApprover) {
            dispatch(warningSnackBar(`Sorry, you don't have permissions to do this action`));
            return;
        }

        let projects = projectData;
        if (!projects) {
            projects = await dispatch(getProjectList(null, null, null, null, null, managerId));
            setProjectData(projects || []);
        }

        let projectIds = [managerId];
        Array.isArray(projects) && projects.forEach((project) => projectIds.push(project.id));

        if (writePermission) {
            projectIds = null;
        }

        const params = {
            showRejected: true,
            projectIds,
            // Use the account's real category types — projectMAJOR/projectMINOR permission
            // keys don't cover accounts that renamed their Category codes to custom values.
            ...(projectCategories.length
                ? { allowedModules: projectCategories.filter((c) => !c.is_delete).map((c) => c.type) }
                : {}),
        };

        if (filterParams) {
            if (filterParams.projectType) params.projectType = filterParams.projectType;
            if (filterParams.projectId) params.projectId = filterParams.projectId;
            if (filterParams.startTime) params.startTime = filterParams.startTime;
            if (filterParams.endTime) params.endTime = filterParams.endTime;
            if (filterParams.confirmationStatus && filterParams.confirmationStatus !== "all") {
                params.confirmationStatus = filterParams.confirmationStatus;
            }
            if (filterParams.createdFrom && filterParams.createdFrom !== "all") {
                params.createdFrom = filterParams.createdFrom;
            }
            if (filterParams.resourceId && filterParams.resourceId !== "all") {
                params.userId = filterParams.resourceId;
            }
        }

        const [list, allUsers] = await Promise.all([
            dispatch(getResourceAllocations(params)),
            dispatch(getUserNames()),
        ]);

        // Build users map from all users (getUserNames returns array directly via axios wrapper)
        const userList = Array.isArray(allUsers) ? allUsers : [];
        const usersById =
            (Array.isArray(userList) &&
                userList.reduce((acc, user) => {
                    acc[user.id] = user;
                    return acc;
                }, {})) ||
            {};

        // Resolve audit actor names for ids missing from the company user list (e.g. global admins)
        const trackedUserIds = [
            ...new Set(
                (Array.isArray(list) ? list : [])
                    .flatMap((allocation) => [allocation.created_by, allocation.updated_by])
                    .filter(Boolean)
            ),
        ].filter((id) => !usersById[id]);

        const extraUsersById = {};
        await Promise.all(
            trackedUserIds.map(async (id) => {
                const res = await Promise.resolve(dispatch(getUser(id))).catch(() => null);
                const payload = res && (res.data || res);
                if (payload) {
                    const name = `${payload.firstName || ""} ${payload.lastName || ""}`.trim();
                    const resolved = name || payload.fullName || payload.email || "";
                    if (resolved) extraUsersById[id] = resolved;
                }
            })
        );

        const resolveUserName = (uid) => {
            if (!uid) return "";
            const user = usersById[uid];
            if (user) {
                return `${user.firstName || ""} ${user.lastName || ""}`.trim() || `Inactive User (${uid})`;
            }
            return extraUsersById[uid] || `Inactive User (${uid})`;
        };

        const resourceAllocations = [];
        Array.isArray(list) &&
            list.forEach((allocation) => {
                const projectName =
                    allocation.projectType === "SERVICE"
                        ? idx(allocation, (_) => _.service.name) || ""
                        : idx(allocation, (_) => _.project.name) || "";

                const date = moment.parseZone(allocation.startTime).format("DD-MM-YYYY");
                const start = moment.parseZone(allocation.startTime).format("HH:mm");
                const end = moment.parseZone(allocation.endTime).format("HH:mm");
                const allocationTime = `${date} ${start} to ${end}`;

                const user = usersById[allocation.userId] || {};
                const empName =
                    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                    (user?.is_global === 1 ? "Global User" : "Unknown User");

                const entryMs = new Date(allocation.endTime) - new Date(allocation.startTime);
                resourceAllocations.push({
                    id: allocation.id,
                    allocationTime,
                    projectName,
                    taskName: allocation.taskName,
                    empName,
                    allocationResourceId: allocation.id,
                    status: allocation.status,
                    comment: allocation.comment,
                    work_hour_breakdown: allocation.work_hour_breakdown,
                    entryHours: entryMs > 0 ? entryMs / (1000 * 3600) : 0,
                    f_taskName: allocation.f_taskName,
                    isConfirmedByStaff: allocation.isConfirmedByStaff,
                    isAllocated: allocation.isAllocated,
                    createdFrom: allocation.createdFrom || "web",
                    isSelected: false,
                    userId: allocation.userId,
                    projectId: allocation.projectId || allocation.project?.id || null,
                    projectType: allocation.projectType || null,
                    startTime: allocation.startTime,
                    endTime: allocation.endTime,
                    approvedStartTime: allocation.approvedStartTime,
                    approvedEndTime: allocation.approvedEndTime,
                    hourlyRate: allocation.hourlyRate,
                    overtime_1: allocation.overtime_1,
                    overtime_2: allocation.overtime_2,
                    requiresManualReview: allocation.requiresManualReview,
                    globalHrs: allocation.globalHrs,
                    isGlobal: user?.is_global === 1,
                    createdAt: allocation.createdAt,
                    createdBy: allocation.created_by,
                    updatedAt: allocation.updated_date,
                    updatedBy: allocation.updated_by,
                    createdByName: resolveUserName(allocation.created_by),
                    updatedByName: resolveUserName(allocation.updated_by),
                });
            });

        // Filter by status if a specific status is selected (not "all")
        let filteredAllocations =
            filterParams?.status && filterParams.status !== "all"
                ? resourceAllocations.filter((allocation) => allocation.status === filterParams.status)
                : resourceAllocations;

        // Filter by confirmation status if selected (confirmed / unconfirmed)
        if (filterParams?.confirmationStatus && filterParams.confirmationStatus !== "all") {
            if (filterParams.confirmationStatus === "confirmed") {
                filteredAllocations = filteredAllocations.filter((allocation) => Boolean(allocation.isConfirmedByStaff));
            } else if (filterParams.confirmationStatus === "unconfirmed") {
                filteredAllocations = filteredAllocations.filter((allocation) => !Boolean(allocation.isConfirmedByStaff));
            }
        }

        // Filter by submission source if selected (web / mobile)
        if (filterParams?.createdFrom && filterParams.createdFrom !== "all") {
            filteredAllocations = filteredAllocations.filter(
                (allocation) => allocation.createdFrom === filterParams.createdFrom
            );
        }

        // Build resource selection from all users (not just those with allocations)
        // This allows filtering by any user, even if they don't have allocations in current view
        const resourceSelection = [
            { key: "All", value: "all" },
            ...Object.values(usersById)
                .map((user) => ({
                    key: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
                    value: user.id,
                }))
                .filter((item) => item.key.trim() !== ""), // Filter out empty names
        ];

        setResourceOptions(resourceSelection);
        setSelectAll(false);
        setSelectedCount(0);
        setAllocations(filteredAllocations);
    };

    const handleMenu = (rowData, event) => {
        setAnchorEl(event?.currentTarget);
        setAllocationId(rowData.id);
        setTimesheetStatus(rowData.status);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleViewComment = () => {
        const selectedAllocation = allocations.find(a => a.id === allocationId);
        if (selectedAllocation && selectedAllocation.comment) {
            setSelectedComment(selectedAllocation.comment);
            setViewCommentModalOpen(true);
        }
        handleClose();
    };

    const modify = () => {
        setIsViewMode(false);
        setShowAllocateResource(true);
        setAnchorEl(null);
    };

    const view = () => {
        setIsViewMode(true);
        setShowAllocateResource(true);
        setAnchorEl(null);
    };

    const updateTimesheetStatus = async (status) => {
        handleClose();
        const selectedAllocation = allocations.find((a) => a.id === allocationId);
        const isManualPending =
            selectedAllocation?.status === "PENDING" && Boolean(selectedAllocation?.requiresManualReview);

        if (status === "APPROVED" && isManualPending) {
            dispatch(
                warningSnackBar(
                    "This record requires manual review before approval. Use Review to rebalance Normal/OT hours."
                )
            );
            setManualReviewAllocation(selectedAllocation);
            setManualReviewModalOpen(true);
            return;
        }

        setPendingAction(status);
        setIsBulkContext(false);
        setActionConfirmationState(true);
    };

    /**
     * Formats bulk recalculation results into user-facing status messages.
     *
     * @param {Object} result - Recalculation API response payload.
     * @returns {{ hasIssues: boolean, message: string }} Summary status flag and formatted message.
     */
    const formatRecalculationSummary = (result) => {
        const data = result?.data || result || {};
        const recalculated = data.recalculated || [];
        const skipped = data.skipped || [];
        const failed = data.failed || [];
        const issues = [...skipped, ...failed]
            .map((item) => {
                const alloc = allocations.find((a) => a.id === item.id);
                const label = alloc
                    ? `${moment.parseZone(alloc.startTime).format("DD-MM-YYYY HH:mm")} to ${moment.parseZone(alloc.endTime).format("HH:mm")}`
                    : item.id;
                return `${label} — ${item.reason}`;
            })
            .join('; ');

        return {
            hasIssues: issues.length > 0,
            message: issues.length > 0
                ? `Recalculated ${recalculated.length} timesheet record(s). ${issues}`
                : result?.message || `Recalculated ${recalculated.length} timesheet record(s).`,
        };
    };

    const requestRecalculation = (ids) => {
        const pendingIds = ids.filter((id) => allocations.some((allocation) => allocation.id === id && allocation.status === "PENDING"));
        if (!pendingIds.length) {
            dispatch(warningSnackBar("Select at least one pending timesheet to recalculate."));
            return;
        }
        handleClose();
        setRecalculateIds(pendingIds);
        setRecalculateConfirmationOpen(true);
    };

    const runRecalculation = async (ids) => {
        if (!ids.length || isRecalculating) return;
        setIsRecalculating(true);
        try {
            const result = await dispatch(recalculateTimesheets(ids));
            const summary = formatRecalculationSummary(result);
            const recalculatedIds = new Set(((result?.data || result || {}).recalculated || []).map((item) => item.id));
            setAllocations((current) => current.map((allocation) => (
                recalculatedIds.has(allocation.id) ? { ...allocation, isSelected: false } : allocation
            )));
            await getAllocations();
            dispatch(summary.hasIssues ? warningSnackBar(summary.message) : successSnackBar(summary.message));
            setRecalculateConfirmationOpen(false);
            setRecalculateIds([]);
            setBulkAllocationModal(false);
        } catch (error) {
            dispatch(errorSnackBar(error?.response?.data?.message || error?.message || "Failed to recalculate timesheets."));
        } finally {
            setIsRecalculating(false);
        }
    };

    const confirmRecalculation = async () => {
        await runRecalculation(recalculateIds);
    };

    /**
     * Opens manual-review modal for a flagged allocation.
     *
     * @param {Object} allocation - Selected allocation row.
     */
    const openManualReview = (allocation) => {
        if (!allocation) return;
        setManualReviewAllocation(allocation);
        setManualReviewModalOpen(true);
        setAnchorEl(null);
    };

    const closeManualReview = () => {
        setManualReviewModalOpen(false);
        setManualReviewAllocation(null);
    };

    const modifyManualReview = () => {
        if (!manualReviewAllocation) return;
        setAllocationId(manualReviewAllocation.id);
        setIsViewMode(false);
        closeManualReview();
        setShowAllocateResource(true);
    };

    const submitManualReview = async () => {
        if (!manualReviewAllocation?.id) return;
        const data = {
            status: "APPROVED",
            userName: manualReviewAllocation.empName || "",
        };

        try {
            await dispatch(approveTimesheet(manualReviewAllocation.id, data));
            closeManualReview();
            await getAllocations();
        } catch (e) {
            console.error("Failed to complete manual review approval", e);
        }
    };

    const handleReviewCommentSubmit = (comment) => {
        setReviewComment(comment);
        setReviewCommentModalOpen(false);
        setActionConfirmationState(true);
    };

    const confirmStatusUpdate = async (status, comment) => {
        setActionConfirmationState(false);
        const selectedAllocation = allocations.find(a => a.id === allocationId);
        const data = {
            status,
            comment: comment || reviewComment || "",
            userName: selectedAllocation ? selectedAllocation.empName : ""
        };
        try {
            await dispatch(approveTimesheet(allocationId, data));
            getAllocations();
            setPendingAction(null);
            setReviewComment("");
        } catch (e) {
            console.error("Failed to approve timesheet", e);
        }
    };

    const deleteHandler = async () => {
        await dispatch(deleteResource("WORK", allocationId));
        handleClose();
        await getAllocations();
        setDeleteDialogState(false);
    };

    const deleteDialogHandler = () => {
        setDeleteDialogState((prevState) => !prevState);
    };

    const handleBulkUpdate = (rowId, action) => {
        setAllocations((prevAllocations) =>
            prevAllocations.map((allocation) => {
                return allocation.id === rowId ? { ...allocation, isSelected: action } : allocation;
            })
        );
    };

    const selectAllBtnHandler = (selecteData) => {
        setSelectedCount(selecteData.filter((_) => _.status === "PENDING" && _.isSelected).length);
        setBulkAllocationModal(!bulkAllocationModal);
    };

    const handleApprove = () => {
        const selectedManualReviewRecords = allocations.filter(
            (a) => a.status === "PENDING" && a.isSelected && a.requiresManualReview
        );
        if (selectedManualReviewRecords.length > 0) {
            dispatch(
                warningSnackBar(
                    `${selectedManualReviewRecords.length} selected record(s) require manual review and cannot be bulk approved.`
                )
            );
            return;
        }

        setBulkAllocationModal(false);
        setPendingCommentAction("APPROVED");
        setIsBulkContext(true);
        setReviewCommentModalOpen(true);
    };

    const handleDeleteBulk = () => {
        bulkDeleteDialogHandler();
    };

    const handleReject = () => {
        setBulkAllocationModal(false);
        setPendingCommentAction("REJECTED");
        setIsBulkContext(true);
        setReviewCommentModalOpen(true);
    };

    const handleBulkReviewCommentSubmit = (comment) => {
        setReviewComment(comment);
        setReviewCommentModalOpen(false);
        updateBulkTimesheetStatus(pendingCommentAction, comment);
        setPendingCommentAction(null);
    };

    const handleCancel = () => {
        setBulkAllocationModal(false);
    };

    const updateBulkTimesheetStatus = async (status, comment = "") => {
        handleClose();
        const selectedAllocations = allocations.filter((_) => _.status === "PENDING" && _.isSelected);
        const allocationIds = selectedAllocations.map((_) => _.id);
        const userNameMap = {};
        selectedAllocations.forEach(a => {
            userNameMap[a.id] = a.empName;
        });

        const data = {
            allocationIds,
            status,
            comment: comment || reviewComment || "",
            userNameMap
        };
        try {
            await dispatch(bulkUpdateTimesheet(data));
            getAllocations();
            setBulkAllocationModal(false);
            setReviewComment("");
            handleCancel();
        } catch (e) {
            console.error("Failed to bulk update timesheets", e);
        }
    };

    const handlePeriodApproval = (period) => {
        const pendingAllocations = allocations.filter(a => a.status === 'PENDING').map(a => ({
            ...a,
            taskHierachy: a.f_taskName,
            requiresManualReview: a.requiresManualReview,
        }));

        let filtered = [];
        let title = '';

        // If both start and end dates are selected, filter by date range
        // Always respect the period button clicked for the title (no auto-detection)
        let periodCandidates = [];
        if (filterParams?.startTime && filterParams?.endTime) {
            const startDate = moment.utc(filterParams.startTime).startOf('day');
            const endDate = moment.utc(filterParams.endTime).endOf('day');

            periodCandidates = pendingAllocations.filter(t => {
                const allocationDate = moment.utc(t.startTime);
                return allocationDate.isSameOrAfter(startDate) && allocationDate.isSameOrBefore(endDate);
            });

            // Always use the period button clicked for the title (respect user intent)
            switch (period) {
                case 'DAILY':
                    title = `Daily Approval - ${startDate.format('DD MMM')} to ${endDate.format('DD MMM YYYY')}`;
                    break;
                case 'WEEKLY':
                    title = `Weekly Approval - ${startDate.format('DD MMM')} to ${endDate.format('DD MMM YYYY')}`;
                    break;
                case 'MONTHLY':
                    title = `Monthly Approval - ${startDate.format('DD MMM')} to ${endDate.format('DD MMM YYYY')}`;
                    break;
                default:
                    title = `Approval - ${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`;
            }
        } else {
            // No date range selected - use period-based filtering with reference date
            const referenceDate = filterParams?.startTime
                ? moment.utc(filterParams.startTime)
                : filterParams?.endTime
                    ? moment.utc(filterParams.endTime)
                    : moment.utc();

            switch (period) {
                case 'DAILY':
                    periodCandidates = pendingAllocations.filter(t => moment.utc(t.startTime).isSame(referenceDate, 'day'));
                    title = `Daily Approval - ${referenceDate.format('DD MMM YYYY')}`;
                    break;
                case 'WEEKLY':
                    periodCandidates = pendingAllocations.filter(t => moment.utc(t.startTime).isSame(referenceDate, 'week'));
                    const weekStart = moment.utc(referenceDate).startOf('week');
                    const weekEnd = moment.utc(referenceDate).endOf('week');
                    title = `Weekly Approval - ${weekStart.format('DD MMM')} to ${weekEnd.format('DD MMM YYYY')}`;
                    break;
                case 'MONTHLY':
                    periodCandidates = pendingAllocations.filter(t => moment.utc(t.startTime).isSame(referenceDate, 'month'));
                    title = `Monthly Approval - ${referenceDate.format('MMMM YYYY')}`;
                    break;
                default:
                    periodCandidates = pendingAllocations;
                    title = 'Review All Pending Records';
            }
        }

        const manualReviewCandidates = periodCandidates.filter((t) => t.requiresManualReview);
        filtered = periodCandidates.filter((t) => !t.requiresManualReview);

        if (manualReviewCandidates.length > 0) {
            dispatch(
                warningSnackBar(
                    `${manualReviewCandidates.length} record(s) in this period require manual review and were excluded from bulk approval.`
                )
            );
        }

        if (filtered.length === 0) {
            const periodText = filterParams?.startTime && filterParams?.endTime
                ? 'selected date range'
                : `${period.toLowerCase()} period`;
            dispatch(warningSnackBar(`No pending records found for the ${periodText}.`));
            return;
        }

        setBulkApproveTimesheets(filtered);
        setBulkApproveTitle(title);
        setShowBulkApproveDialog(true);
    };

    const renderPeriodicApprovalButtons = () => {
        const hasPending = allocations.some(a => a.status === 'PENDING');
        const hasPermission = permissions && permissions.timesheetApprover === 2;

        if (!hasPending || !hasPermission) return null;

        // Determine which buttons to show based on date range
        let showDaily = true;
        let showWeekly = true;
        let showMonthly = true;

        if (filterParams?.startTime && filterParams?.endTime) {
            const startDate = moment.utc(filterParams.startTime);
            const endDate = moment.utc(filterParams.endTime);
            const daysDiff = endDate.diff(startDate, 'days');

            if (daysDiff === 0) {
                // Single day - only show DAILY
                showWeekly = false;
                showMonthly = false;
            } else if (daysDiff <= 7) {
                // Week or less - show DAILY and WEEKLY
                showMonthly = false;
            }
            // If daysDiff > 7, show all three buttons
        }

        return (
            <div className="d-flex gap-2 flex-wrap">
                {showDaily && (
                    <Button
                        variant="outline-primary"
                        className="secondarybtn secondarybtn-outline btn-ch"
                        onClick={() => handlePeriodApproval('DAILY')}
                        data-cy="approve-daily-btn"
                    >
                        APPROVE DAILY
                    </Button>
                )}
                {showWeekly && (
                    <Button
                        variant="outline-primary"
                        className="secondarybtn secondarybtn-outline btn-ch"
                        onClick={() => handlePeriodApproval('WEEKLY')}
                        data-cy="approve-weekly-btn"
                    >
                        APPROVE WEEKLY
                    </Button>
                )}
                {showMonthly && (
                    <Button
                        variant="outline-primary"
                        className="secondarybtn secondarybtn-outline btn-ch"
                        onClick={() => handlePeriodApproval('MONTHLY')}
                        data-cy="approve-monthly-btn"
                    >
                        APPROVE MONTHLY
                    </Button>
                )}
            </div>
        );
    };

    const bulkDeleteDialogHandler = async () => {
        const toDelete = allocations.filter((a) => a.status === "PENDING" && a.isSelected);
        if (toDelete.length > 0) {
            await dispatch(deleteResources("WORK", { allocationIds: toDelete.map((_) => _.id) }));
        }
        handleClose();
        await getAllocations();
        setBulkAllocationModal(false);
    };

    const statusOptions = STATUS_OPTIONS;
    const selectedAllocation = allocations.find((a) => a.id === allocationId);
    const isManualPendingSelection =
        selectedAllocation?.status === "PENDING" && Boolean(selectedAllocation?.requiresManualReview);

    /**
     * Renders compact KPI mini cards into the table toolbar row alongside search.
     *
     * @returns {React.ReactElement} Compact summary cards container
     */
    const renderSummaryCards = () => (
        <div className="timesheet-summary-cards" data-cy="timesheet-summary-cards">
            <div className="timesheet-summary-card timesheet-summary-card--navy">
                <div className="timesheet-summary-card__value">{reportData?.kpis?.totalWorkers ?? 0}</div>
                <div className="timesheet-summary-card__sub">workers</div>
            </div>
            <div className="timesheet-summary-card timesheet-summary-card--teal">
                <div className="timesheet-summary-card__value">{reportData?.kpis?.totalEntries ?? 0}</div>
                <div className="timesheet-summary-card__sub">entries</div>
            </div>
            <div className="timesheet-summary-card timesheet-summary-card--blue">
                <div className="timesheet-summary-card__value">
                    {numberFormat(reportData?.kpis?.totalHours || 0, true)} hrs
                </div>
                <div className="timesheet-summary-card__sub">across all projects</div>
            </div>
            <div className="timesheet-summary-card timesheet-summary-card--coral">
                <div className="timesheet-summary-card__value">
                    {numberFormat(reportData?.kpis?.totalCost || 0)}
                </div>
                <div className="timesheet-summary-card__sub">total</div>
            </div>
        </div>
    );

    return (
        <>
            <DeleteDialog
                open={deleteDialogState}
                id={allocationId}
                deleteHandler={deleteHandler}
                dialogClose={deleteDialogHandler}
                moduleName="Allocation"
                description="Allocation"
            />
            <ReviewCommentModal
                open={reviewCommentModalOpen}
                onClose={() => {
                    setReviewCommentModalOpen(false);
                    setPendingCommentAction(null);
                    setPendingAction(null);
                    setReviewComment("");
                    setIsBulkContext(false);
                }}
                onSubmit={isBulkContext ? handleBulkReviewCommentSubmit : handleReviewCommentSubmit}
                action={isBulkContext ? pendingCommentAction : pendingAction}
                isBulk={isBulkContext}
                count={selectedCount}
            />
            <BulkAllocationModal
                selectedCount={selectedCount}
                onApprove={handleApprove}
                onDelete={handleDeleteBulk}
                onReject={handleReject}
                onRecalculate={() => runRecalculation(allocations.filter((allocation) => allocation.status === "PENDING" && allocation.isSelected).map((allocation) => allocation.id))}
                onCancel={handleCancel}
                open={bulkAllocationModal}
                dialogClose={handleCancel}
                processing={isRecalculating}
            />
            <BulkApproveDialog
                open={showBulkApproveDialog}
                onClose={() => setShowBulkApproveDialog(false)}
                timesheets={bulkApproveTimesheets}
                onSuccess={() => {
                    setShowBulkApproveDialog(false);
                    getAllocations();
                }}
                title={bulkApproveTitle}
            />
            <Modal
                open={viewCommentModalOpen}
                onClose={() => {
                    setViewCommentModalOpen(false);
                    setSelectedComment("");
                }}
                fullWidth={true}
                maxWidth="sm"
                modalHeading="Review Comment"
                modalCloseTxt="Close"
                onClick={() => {
                    setViewCommentModalOpen(false);
                    setSelectedComment("");
                }}
                showActionBtn
                cxlbtnClass="cancelBtn"
                primary
            >
                <div style={{
                    padding: '16px 0',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: '#333',
                    minHeight: '100px',
                    maxHeight: '400px',
                    textAlign: 'justify',
                    overflowY: 'auto'
                }}>
                    {selectedComment || 'No comment available.'}
                </div>
            </Modal>
            <TimesheetActionConfirmation
                open={actionConfirmationState}
                dialogClose={() => setActionConfirmationState(false)}
                onConfirm={confirmStatusUpdate}
                action={pendingAction}
                timesheetId={allocationId}
            />
            <TimesheetActionConfirmation
                open={recalculateConfirmationOpen}
                dialogClose={() => {
                    if (!isRecalculating) {
                        setRecalculateConfirmationOpen(false);
                        setRecalculateIds([]);
                    }
                }}
                onConfirm={confirmRecalculation}
                action="RECALCULATE"
                count={recalculateIds.length}
                isBulk={recalculateIds.length > 1}
                hideCommentField
            />
            <ManualReviewHoursModal
                open={manualReviewModalOpen}
                allocation={manualReviewAllocation}
                onClose={closeManualReview}
                onSubmit={submitManualReview}
                onModify={modifyManualReview}
            />
            <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                {selectedAllocation && (
                    <MenuItem onClick={() => openManualReview(selectedAllocation)} data-cy="action-review">
                        Review
                    </MenuItem>
                )}
                {timesheetStatus === "PENDING" && (
                    <MenuItem onClick={modify} data-cy="action-modify">
                        Modify
                    </MenuItem>
                )}
                {timesheetStatus === "PENDING" && !isManualPendingSelection && (
                    <MenuItem onClick={() => updateTimesheetStatus("APPROVED")} data-cy="action-approve">
                        Accept/Approve
                    </MenuItem>
                )}
                {timesheetStatus === "PENDING" && (
                    <MenuItem onClick={() => updateTimesheetStatus("REJECTED")} data-cy="action-reject">
                        Reject
                    </MenuItem>
                )}
                {timesheetStatus === "PENDING" && !isManualPendingSelection && (
                    <MenuItem onClick={() => deleteDialogHandler()} data-cy="action-delete">
                        Delete
                    </MenuItem>
                )}
                {timesheetStatus === "PENDING" && (
                    <MenuItem onClick={() => requestRecalculation([allocationId])} data-cy="action-recalculate">
                        Recalculate
                    </MenuItem>
                )}
                {timesheetStatus !== "PENDING" && (
                    <MenuItem onClick={view}>View</MenuItem>
                )}
                {timesheetStatus !== "PENDING" && (
                    <MenuItem onClick={() => updateTimesheetStatus("PENDING")}>Set as Pending</MenuItem>
                )}
                {selectedAllocation?.comment && <MenuItem onClick={handleViewComment}>View Comment</MenuItem>}
            </Menu>
            {showAllocateResource && (
                <EditTimesheetDialog
                    open={showAllocateResource}
                    onClose={() => {
                        setShowAllocateResource(false);
                        setIsViewMode(false);
                    }}
                    allocationId={allocationId}
                    staff={selectedAllocation?.userId ? {
                        id: selectedAllocation.userId,
                        name: selectedAllocation.empName,
                        initials: (selectedAllocation.empName || 'U').slice(0, 2).toUpperCase(),
                    } : null}
                    date={selectedAllocation?.startTime ? moment.parseZone(selectedAllocation.startTime).format('YYYY-MM-DD') : ''}
                    projectId={selectedAllocation?.projectId || null}
                    isViewMode={isViewMode}
                    projects={projectData || []}
                    onSuccess={getAllocations}
                />
            )}
            <Grid container sx={{ m: 0, mb: 1 }}>
                <Grid item xs={12} sx={{ p: 0 }} textAlign={"start"}>
                    <span className="link">
                        Timesheet
                    </span>
                    <span className="arrow"> {"/"} </span>
                    {activeTab === "reports" ? "Report" : "Allocations"}
                </Grid>
            </Grid>

            <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                className="timesheet-mode-tabs"
            >
                <Tab
                    label="Allocations"
                    value="allocations"
                    data-cy="timesheet-tab-allocations"
                />
                <Tab
                    label="Report"
                    value="reports"
                    data-cy="timesheet-tab-reports"
                />
            </Tabs>

            <div className="mb-3">
                <Filter
                    applyFilter={applyFilter}
                    onProjectsLoaded={setProjectData}
                    showReportButton={false}
                    timesheetFilter={true}
                    showResourceFilter={true}
                    showStatusFilter={true}
                    resourceOptions={resourceOptions}
                    statusOptions={statusOptions}
                    extraActions={activeTab === "allocations" ? renderPeriodicApprovalButtons() : null}
                />
            </div>

            <div className="tableContainer timesheetScrollMobile">
                <div className="timesheetScroll">
                    <div className="mobileTimesheet">
                        {activeTab === "allocations" ? (
                            <CustomTable
                                columns={columns}
                                data={allocations}
                                isAction={true}
                                handleMenu={(data, event) => handleMenu(data, event)}
                                selectAllAction={true}
                                selectAllBtnHandler={selectAllBtnHandler}
                                selectAll={selectAll}
                                actionBtnDataCy="action-menu-trigger"
                                toolbarLeftContent={renderSummaryCards()}
                                menu={(data) => (
                                    <Popover id="popover-basic" data-cy="action-menu-popover">
                                        <Popover.Body>
                                            <ul className="action-block-list">
                                                <li onClick={() => openManualReview(data)} data-cy="action-review">Review</li>
                                                {data.status === "PENDING" && <li onClick={modify} data-cy="action-modify">Modify</li>}
                                                {data.status === "PENDING" && !data.requiresManualReview && (
                                                    <li onClick={() => updateTimesheetStatus("APPROVED")} data-cy="action-approve">Accept/Approve</li>
                                                )}
                                                {data.status === "PENDING" && (
                                                    <li onClick={() => updateTimesheetStatus("REJECTED")} data-cy="action-reject">Reject</li>
                                                )}
                                                {data.status === "PENDING" && !data.requiresManualReview && (
                                                    <li onClick={() => deleteDialogHandler()} data-cy="action-delete">Delete</li>
                                                )}
                                                {data.status === "PENDING" && (
                                                    <li onClick={() => requestRecalculation([data.id])} data-cy="action-recalculate">Recalculate</li>
                                                )}
                                                {data.status !== "PENDING" && (
                                                    <li onClick={view} data-cy="action-view">View</li>
                                                )}
                                                {data.status !== "PENDING" && (
                                                    <li onClick={() => updateTimesheetStatus("PENDING")} data-cy="action-pending">Set as Pending</li>
                                                )}
                                                {data.comment && (
                                                    <li onClick={handleViewComment} data-cy="action-view-comment">View Comment</li>
                                                )}
                                            </ul>
                                        </Popover.Body>
                                    </Popover>
                                )}
                                fileName="Allocation List"
                            />
                        ) : (
                            <TimesheetReportTable
                                data={reportData.summary}
                                toolbarLeftContent={renderSummaryCards()}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AllocationsView;

